import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api';
import { useToast } from '../components/Toast';
import { useProfile, useUsage, useCpvSearch, useCpvLabels, QK } from '../queries';
import { useIsMobile } from '../hooks/useWindowWidth';
import { PROCUREMENT_TYPE_OPTIONS, CONTRACT_SIZE_OPTIONS } from '../constants';
import TagInput from '../components/TagInput';
import { CPV_TREE, CPV_LABELS } from '../data/cpv';
import CountryTransfer from '../components/CountryTransfer';
import RegionSelect from '../components/RegionSelect';
import { computeCompleteness } from '../hooks/useProfileCompleteness';
import UpgradeBanner from '../components/UpgradeBanner';

const s = {
  page: { maxWidth: 720, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  subheading: { fontSize: 15, color: '#64748b', marginBottom: 32 },
  card: { background: '#fff', borderRadius: 12, padding: '28px 32px', border: '1px solid #e2e8f0', marginBottom: 24 },
  cardMobile: { background: '#fff', borderRadius: 12, padding: '20px 16px', border: '1px solid #e2e8f0', marginBottom: 24 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#1e293b', marginBottom: 20, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 },
  rowMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 },
  field: { marginBottom: 20 },
  fieldMobile: { marginBottom: 16 },
  label: { display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 },
  hint: { fontSize: 12, color: '#94a3b8', marginTop: 4 },
  input: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  inputError: {
    width: '100%', padding: '10px 14px', border: '1px solid #f87171',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
  },
  fieldError: { fontSize: 12, color: '#b91c1c', marginTop: 4 },
  select: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', background: '#fff', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none',
    minHeight: 100, resize: 'vertical', boxSizing: 'border-box',
  },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 },
  checkboxLabel: { fontSize: 14, color: '#374151' },
  actions: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  saveBtn: (disabled) => ({
    background: disabled ? '#93c5fd' : '#1e40af', color: '#fff', border: 'none', fontWeight: 600,
    fontSize: 15, padding: '12px 28px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  findBtn: (disabled) => ({
    background: disabled ? '#6ee7b7' : '#059669', color: '#fff', border: 'none', fontWeight: 600,
    fontSize: 15, padding: '12px 28px', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  deleteBtn: {
    background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5',
    fontWeight: 500, fontSize: 14, padding: '12px 20px', borderRadius: 8, cursor: 'pointer', marginTop: 8,
  },
  backLink: {
    display: 'inline-block', marginBottom: 20, fontSize: 14, color: '#64748b', cursor: 'pointer',
    textDecoration: 'none', background: 'none', border: 'none', padding: 0,
  },
  loadingPage: { textAlign: 'center', padding: 80, color: '#64748b' },
  completenessCard: { background: '#fff', borderRadius: 12, padding: '20px 32px', border: '1px solid #e2e8f0', marginBottom: 24 },
  completenessCardMobile: { background: '#fff', borderRadius: 12, padding: '16px', border: '1px solid #e2e8f0', marginBottom: 24 },
  completenessTitle: { fontSize: 13, fontWeight: 500, color: '#64748b', marginBottom: 6 },
  progressBarWrap: { background: '#e2e8f0', borderRadius: 8, height: 8, marginBottom: 10 },
  progressBar: (pct) => ({
    background: pct >= 80 ? '#16a34a' : pct >= 50 ? '#f59e0b' : '#dc2626',
    width: pct + '%', height: '100%', borderRadius: 8, transition: 'width 0.3s',
  }),
  gapList: { display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 4 },
  gapItem: { fontSize: 12, color: '#94a3b8' },
  pillToggle: (selected) => ({
    padding: '5px 13px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
    border: selected ? '2px solid #1e40af' : '1px solid #d1d5db',
    background: selected ? '#eff6ff' : '#fff',
    color: selected ? '#1e40af' : '#374151',
    fontWeight: selected ? 600 : 400,
  }),
  pillWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  checkboxGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginTop: 4 },
  checkboxGridMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 4 },
  dangerZone: { background: '#fff', borderRadius: 12, padding: '20px 32px', border: '1px solid #fca5a5', marginBottom: 24 },
  dangerTitle: { fontSize: 14, fontWeight: 600, color: '#b91c1c', marginBottom: 8 },
  dangerText: { fontSize: 13, color: '#64748b', marginBottom: 12 },
};

const EMPTY_FORM = {
  profile_name: '',
  specialisations: [],
  capabilities_description: '',
  reference_projects: '',
  contract_size_min: '', contract_size_max: '',
  operating_countries: [], operating_regions: [], keywords: '',
  cpv_interests: [], procurement_types: [],
};


function CpvPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  // Full-table search (9,453 official codes) alongside the curated tree, and
  // full-table labels for selected codes the curated map doesn't know
  // (onboarding suggestions are specific 8-digit codes).
  const searchQuery = useCpvSearch(query);
  const unknownCodes = value.filter((c) => !CPV_LABELS[c]);
  const labelsQuery = useCpvLabels(unknownCodes);
  const allLabels = { ...CPV_LABELS, ...(labelsQuery.data?.labels || {}) };

  function toggle(code) {
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  }

  function toggleExpand(code) {
    setExpanded((e) => ({ ...e, [code]: !e[code] }));
  }

  const q = query.toLowerCase();
  const filtered = q
    ? CPV_TREE.filter((div) =>
        div.label.toLowerCase().includes(q) || div.code.includes(q) ||
        div.groups.some((g) => g.label.toLowerCase().includes(q) || g.code.includes(q))
      )
    : CPV_TREE;

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {value.map((code) => (
            <span key={code} style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: 12, padding: '3px 10px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
              <strong>{code}</strong> {allLabels[code] || ''}
              <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93c5fd', fontWeight: 700, fontSize: 14, padding: 0, lineHeight: 1 }} onClick={() => toggle(code)}>×</button>
            </span>
          ))}
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#94a3b8', padding: '3px 0' }} onClick={() => onChange([])}>Clear all</button>
        </div>
      )}
      <input
        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#1e293b', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
        placeholder="Search categories or the full CPV catalogue (code or keyword)…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {query.trim().length >= 3 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, margin: '2px 0 6px' }}>
            Full CPV catalogue
          </div>
          {(() => {
            const results = (searchQuery.data?.results || []).filter((r) => !value.includes(r.code));
            if (searchQuery.isLoading) return <div style={{ fontSize: 12, color: '#94a3b8' }}>Searching…</div>;
            if (results.length === 0) return <div style={{ fontSize: 12, color: '#94a3b8' }}>No catalogue matches</div>;
            return (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 180, overflowY: 'auto' }}>
                {results.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    style={{ display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: 'none', borderBottom: '1px solid #f1f5f9', padding: '7px 12px', cursor: 'pointer', fontSize: 12, color: '#374151' }}
                    onClick={() => toggle(r.code)}
                  >
                    <strong style={{ color: '#64748b' }}>{r.code}</strong> — {r.label}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}
      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        {filtered.map((div) => {
          const divSelected = value.includes(div.code);
          const hasGroups = div.groups.length > 0;
          const isOpen = expanded[div.code] || (q && div.groups.some((g) => g.label.toLowerCase().includes(q) || g.code.includes(q)));
          return (
            <div key={div.code}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f1f5f9', gap: 8, background: divSelected ? '#eff6ff' : '#fff' }}>
                {hasGroups ? (
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: 12, width: 16, flexShrink: 0 }} onClick={() => toggleExpand(div.code)}>{isOpen ? '▾' : '▸'}</button>
                ) : <span style={{ width: 16, flexShrink: 0 }} />}
                <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: 0 }} onClick={() => toggle(div.code)}>
                  <span style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, border: `2px solid ${divSelected ? '#1d4ed8' : '#d1d5db'}`, background: divSelected ? '#1d4ed8' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {divSelected && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                  </span>
                  <span style={{ fontSize: 13, color: '#1e293b' }}><strong style={{ color: '#374151' }}>{div.code}</strong> — {div.label}</span>
                </button>
              </div>
              {isOpen && div.groups.map((grp) => {
                const grpSelected = value.includes(grp.code);
                return (
                  <div key={grp.code} style={{ display: 'flex', alignItems: 'center', padding: '7px 12px 7px 36px', borderBottom: '1px solid #f8fafc', gap: 8, background: grpSelected ? '#eff6ff' : '#f8fafc' }}>
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: 0 }} onClick={() => toggle(grp.code)}>
                      <span style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0, border: `2px solid ${grpSelected ? '#1d4ed8' : '#d1d5db'}`, background: grpSelected ? '#1d4ed8' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {grpSelected && <span style={{ color: '#fff', fontSize: 9, lineHeight: 1 }}>✓</span>}
                      </span>
                      <span style={{ fontSize: 12, color: '#374151' }}><strong style={{ color: '#64748b' }}>{grp.code}</strong> — {grp.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProfileForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const showToast = useToast();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const profileQuery = useProfile(id, { enabled: isEditMode });
  const usageQuery = useUsage({ enabled: !isEditMode });
  const usage = usageQuery.data ?? null;

  const [saving, setSaving] = useState(false);
  const [findingMatches, setFindingMatches] = useState(false);
  const [mode, setMode] = useState('form'); // 'form' | 'created' | 'finding'
  const [savedProfileId, setSavedProfileId] = useState(null);
  const [matchProgress, setMatchProgress] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const seededRef = useRef(false);

  // Seed the editable form once from the cached/fetched profile (edit mode).
  // The ref guard stops a background refetch from clobbering in-progress edits.
  useEffect(() => {
    if (!isEditMode || seededRef.current || profileQuery.isLoading) return;
    if (profileQuery.data) setForm(profileQuery.data);
    seededRef.current = true;
  }, [isEditMode, profileQuery.isLoading, profileQuery.data]);

  useEffect(() => {
    if (profileQuery.error) showToast('Failed to load profile: ' + profileQuery.error.message, 'error');
  }, [profileQuery.error]); // eslint-disable-line

  const loading = isEditMode && profileQuery.isLoading;

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((e) => ({ ...e, [field]: null }));
  }

  function validate() {
    const errors = {};
    if (!form.profile_name.trim()) errors.profile_name = 'Profile name is required';
    // On a capped tier, POST /profiles rejects an empty country list — empty means
    // "all EU", which the plan isn't paying for. Enforced on CREATE only (edit
    // stays lenient so existing all-EU profiles remain editable), so mirror that
    // here rather than letting it arrive as a bare 403 toast.
    if (!isEditMode
        && typeof usage?.countries_max === 'number'
        && (form.operating_countries || []).length === 0) {
      errors.operating_countries = 'Pick at least one country so we know which tenders to show you';
    }
    return errors;
  }

  async function save() {
    const method = isEditMode ? 'PUT' : 'POST';
    const url = isEditMode ? `/profiles/${id}` : '/profiles';
    return apiFetch(url, { method, body: JSON.stringify(form) });
  }

  async function handleSave(e) {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setSaving(true);
    try {
      const saved = await save();
      queryClient.invalidateQueries({ queryKey: QK.profiles });
      queryClient.invalidateQueries({ queryKey: QK.matches });
      queryClient.invalidateQueries({ queryKey: QK.usage });
      if (isEditMode) queryClient.invalidateQueries({ queryKey: QK.profile });
      if (isEditMode) {
        showToast('Profile saved.', 'success');
      } else {
        setSavedProfileId(saved.id);
        setMode('created');
      }
      return saved;
    } catch (err) {
      showToast('Failed to save profile: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleFindMatches() {
    setFindingMatches(true);
    setMatchProgress(0);
    const startTime = Date.now();
    const timer = setInterval(() => {
      setMatchProgress(Math.min(80, ((Date.now() - startTime) / 4000) * 80));
    }, 100);
    try {
      const result = await apiFetch(`/profiles/${id}/find-matches`, { method: 'POST' });
      clearInterval(timer);
      setMatchProgress(100);
      queryClient.invalidateQueries({ queryKey: QK.matches });
      const count = result?.matches_found ?? 0;
      showToast(`Found ${count} potential match${count !== 1 ? 'es' : ''}.`, 'success');
      navigate(`/dashboard?profile_id=${id}`);
    } catch (err) {
      clearInterval(timer);
      setMatchProgress(0);
      showToast('Failed to find matches: ' + err.message, 'error');
    } finally {
      setFindingMatches(false);
    }
  }

  async function handleFindMatchesFromCreate() {
    const profileId = savedProfileId;
    setMode('finding');
    setMatchProgress(0);
    const startTime = Date.now();
    const timer = setInterval(() => {
      setMatchProgress(Math.min(80, ((Date.now() - startTime) / 4000) * 80));
    }, 100);
    try {
      const result = await apiFetch(`/profiles/${profileId}/find-matches`, { method: 'POST' });
      clearInterval(timer);
      setMatchProgress(100);
      queryClient.invalidateQueries({ queryKey: QK.matches });
      const count = result?.matches_found ?? 0;
      showToast(`Found ${count} potential match${count !== 1 ? 'es' : ''}.`, 'success');
      setTimeout(() => navigate(`/dashboard?profile_id=${profileId}`), 400);
    } catch (err) {
      clearInterval(timer);
      setMode('created');
      setMatchProgress(0);
      showToast('Failed to find matches: ' + err.message, 'error');
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete profile "${form.profile_name}"? This will remove all associated matches.`)) return;
    try {
      await apiFetch(`/profiles/${id}`, { method: 'DELETE' });
      queryClient.invalidateQueries({ queryKey: QK.profiles });
      queryClient.invalidateQueries({ queryKey: QK.matches });
      queryClient.invalidateQueries({ queryKey: QK.usage });
      showToast('Profile deleted', 'success');
      navigate('/profiles');
    } catch (err) {
      showToast('Failed to delete profile: ' + err.message, 'error');
    }
  }

  if (loading) return <div style={s.loadingPage}>Loading profile…</div>;

  if (mode === 'created' || mode === 'finding') {
    return (
      <div style={{ maxWidth: 520, margin: '72px auto', padding: '0 24px' }}>
        <div style={{
          background: '#fff',
          borderRadius: 14,
          border: '2px solid #0A1F4A',
          padding: '52px 48px',
          textAlign: 'center',
          boxShadow: '0 6px 0 0 #0A1F4A',
        }}>
          <div style={{ fontSize: 38, marginBottom: 16, color: '#16a34a' }}>✓</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#0A1F4A', marginBottom: 10 }}>
            Profile created!
          </h1>
          <p style={{ fontSize: 15, color: '#64748b', marginBottom: 36, lineHeight: 1.6 }}>
            <strong style={{ color: '#0A1F4A' }}>{form.profile_name}</strong> is ready.{' '}
            Let's find your matching EU tenders.
          </p>

          {mode === 'finding' ? (
            <div>
              <div style={{ background: '#ECEEF3', borderRadius: 8, height: 10, marginBottom: 10, overflow: 'hidden' }}>
                <div style={{
                  background: '#FF7A3D',
                  width: `${matchProgress}%`,
                  height: '100%',
                  borderRadius: 8,
                  transition: 'width 0.2s ease',
                }} />
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>Searching for matching tenders…</p>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={handleFindMatchesFromCreate}
                style={{
                  background: '#FF7A3D',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700,
                  fontSize: 16,
                  padding: '14px 0',
                  borderRadius: 8,
                  cursor: 'pointer',
                  boxShadow: '0 4px 0 0 #0A1F4A',
                  width: '100%',
                  maxWidth: 280,
                  display: 'block',
                  margin: '0 auto',
                }}
              >
                Find matches →
              </button>
              <div style={{ marginTop: 20 }}>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#94a3b8', textDecoration: 'underline' }}
                  onClick={() => navigate(`/profiles/${savedProfileId}`)}
                >
                  Edit profile first
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const card = isMobile ? s.cardMobile : s.card;
  const row = isMobile ? s.rowMobile : s.row;
  const field = isMobile ? s.fieldMobile : s.field;
  const { score, missing } = computeCompleteness(form);
  const atLimit = !isEditMode && usage && usage.profiles_max > 0 && usage.profiles_active >= usage.profiles_max;

  return (
    <div style={s.page}>
      <button style={s.backLink} onClick={() => navigate('/profiles')}>← Back to profiles</button>
      <h1 style={s.heading}>{isEditMode ? 'Edit Profile' : 'New Search Profile'}</h1>
      <p style={s.subheading}>
        {isEditMode
          ? 'Update this profile to refine your tender matches.'
          : 'Create a profile to find matching EU tenders for this product or service area.'}
      </p>

      {atLimit && (
        <UpgradeBanner
          title="Profile limit reached"
          message={`Your plan allows ${usage.profiles_max} active profile${usage.profiles_max !== 1 ? 's' : ''}. Upgrade to add more.`}
          cta="Upgrade plan"
        />
      )}

      <form onSubmit={handleSave}>
        <div style={isMobile ? s.completenessCardMobile : s.completenessCard}>
          <div style={s.completenessTitle}>Profile completeness: {score}%</div>
          <div style={s.progressBarWrap}><div style={s.progressBar(score)} /></div>
          {missing.length > 0 && (
            <div style={s.gapList}>
              {missing.map((g) => <span key={g.key} style={s.gapItem}>+ {g.label}</span>)}
            </div>
          )}
        </div>

        <div style={card}>
          <div style={s.sectionTitle}>Profile Identity</div>
          <div style={field}>
            <label style={s.label}>Profile name *</label>
            <input
              style={fieldErrors.profile_name ? s.inputError : s.input}
              value={form.profile_name}
              onChange={(e) => set('profile_name', e.target.value)}
              placeholder="e.g. IT Services Germany, Construction EU"
            />
            {fieldErrors.profile_name && <div style={s.fieldError}>{fieldErrors.profile_name}</div>}
            <div style={s.hint}>Give this profile a name that describes what kind of tenders you're looking for</div>
          </div>
        </div>

        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 18px', marginBottom: 24, fontSize: 14, color: '#0369a1' }}>
          Company info (name, country, size) is managed in{' '}
          <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e40af', fontWeight: 600, padding: 0, fontSize: 14 }} onClick={() => navigate('/profile')}>Company Settings</button>.
          Each search profile here targets a specific product or service area.
        </div>

        <div style={card}>
          <div style={s.sectionTitle}>Expertise &amp; Capabilities</div>
          <div style={field}>
            <label style={s.label}>Specialisations</label>
            <TagInput value={form.specialisations} onChange={(v) => set('specialisations', v)} placeholder="Type and press Enter (e.g. Cloud Infrastructure)" />
            <div style={s.hint}>Press Enter or comma to add each specialisation</div>
          </div>
          <div style={field}>
            <label style={s.label}>What you do (capabilities)</label>
            <textarea
              style={s.textarea}
              value={form.capabilities_description || ''}
              onChange={(e) => set('capabilities_description', e.target.value)}
              placeholder="The concrete products, services and technologies you deliver. Be specific — e.g. 'Design and build of GDPR-compliant data pipelines and FHIR interoperability layers between hospital EHR systems, on Azure.'"
            />
            <div style={s.hint}>The single strongest driver of match quality — name real products, technologies and the sectors you serve.</div>
          </div>
          <div style={field}>
            <label style={s.label}>Past projects &amp; clients</label>
            <textarea
              style={s.textarea}
              value={form.reference_projects || ''}
              onChange={(e) => set('reference_projects', e.target.value)}
              placeholder="2–3 concrete things you've delivered — what, for whom, in what sector. e.g. 'Built the national e-invoicing portal for the Dutch Tax Authority (2M+ users).'"
            />
            <div style={s.hint}>What you've delivered is the best predictor of what you can win — and it reads like a tender.</div>
          </div>
          <div style={row}>
            <div>
              <label style={s.label}>Smallest contract</label>
              <select style={s.select} value={form.contract_size_min || ''} onChange={(e) => set('contract_size_min', e.target.value)}>
                {CONTRACT_SIZE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Largest contract</label>
              <select style={s.select} value={form.contract_size_max || ''} onChange={(e) => set('contract_size_max', e.target.value)}>
                {CONTRACT_SIZE_OPTIONS.map((o) => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div style={s.hint}>Optional — the contract sizes you can realistically deliver. Used to skip tenders far outside your capacity.</div>
        </div>

        <div style={card}>
          <div style={s.sectionTitle}>Target Markets</div>
          <div style={field}>
            <label style={s.label}>Operating countries</label>
            <CountryTransfer value={form.operating_countries} onChange={(v) => set('operating_countries', v)} isMobile={isMobile} />
            {fieldErrors.operating_countries && <div style={s.fieldError}>{fieldErrors.operating_countries}</div>}
            <div style={s.hint}>
              {!isEditMode && typeof usage?.countries_max === 'number'
                ? `Drag countries to Include or Exclude. Your plan covers ${usage.countries_max} ${usage.countries_max === 1 ? 'country' : 'countries'}.`
                : 'Drag countries to Include or Exclude. Leave both empty to see all countries.'}
            </div>
          </div>
          <div style={field}>
            <label style={s.label}>Delivery regions <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
            <RegionSelect
              operatingCountries={form.operating_countries}
              value={form.operating_regions}
              onChange={(v) => set('operating_regions', v)}
            />
            <div style={s.hint}>Narrow delivery to specific provinces where you actually operate. Leave a country untouched to match it nationwide.</div>
          </div>
          <div style={field}>
            <label style={s.label}>Summary — what you deliver &amp; to whom</label>
            <textarea
              style={s.textarea}
              value={form.keywords}
              onChange={(e) => set('keywords', e.target.value)}
              placeholder="A short supply-side summary of the work you deliver and who you serve. e.g. 'We deliver public-sector cloud migrations, cybersecurity audits and GDPR-compliance projects for government bodies and municipalities.'"
            />
            <div style={s.hint}>Describe what you <strong>deliver</strong>, not the tenders you're searching for — this is embedded against tender descriptions, so supply-side wording matches best.</div>
          </div>
        </div>

        <div style={card}>
          <div style={s.sectionTitle}>Tender Preferences</div>
          <div style={field}>
            <label style={s.label}>CPV areas of interest</label>
            <CpvPicker value={form.cpv_interests} onChange={(v) => set('cpv_interests', v)} />
            <div style={s.hint}>Select divisions or specific groups — used to filter and rank tenders</div>
          </div>
          <div style={field}>
            <label style={s.label}>Procurement types</label>
            <div style={isMobile ? s.checkboxGridMobile : s.checkboxGrid}>
              {PROCUREMENT_TYPE_OPTIONS.map((opt) => {
                const checked = form.procurement_types.includes(opt);
                return (
                  <div key={opt} style={s.checkboxRow}>
                    <input
                      type="checkbox"
                      id={`pt_${opt}`}
                      checked={checked}
                      onChange={() => set('procurement_types', checked
                        ? form.procurement_types.filter((t) => t !== opt)
                        : [...form.procurement_types, opt])}
                    />
                    <label htmlFor={`pt_${opt}`} style={s.checkboxLabel}>{opt}</label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={s.actions}>
          <button style={s.saveBtn(saving || findingMatches)} type="submit" disabled={saving || findingMatches}>
            {saving ? 'Saving...' : isEditMode ? 'Save profile' : 'Create profile'}
          </button>
          {isEditMode && (
            <button style={s.findBtn(findingMatches || saving)} type="button" onClick={handleFindMatches} disabled={findingMatches || saving}>
              {findingMatches ? 'Finding matches...' : 'Find matches'}
            </button>
          )}
        </div>
        {isEditMode && findingMatches && (
          <div style={{ marginTop: 14, width: '100%' }}>
            <div style={{ background: '#ECEEF3', borderRadius: 6, height: 6, overflow: 'hidden' }}>
              <div style={{
                background: '#FF7A3D',
                width: `${matchProgress}%`,
                height: '100%',
                borderRadius: 6,
                transition: 'width 0.2s ease',
              }} />
            </div>
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Searching for matching tenders…</p>
          </div>
        )}
        {isEditMode && !findingMatches && (
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
            Find matches searches for tenders using your profile. Results appear on the dashboard.
          </p>
        )}
      </form>

      {isEditMode && (
        <div style={{ ...s.dangerZone, marginTop: 32 }}>
          <div style={s.dangerTitle}>Delete profile</div>
          <div style={s.dangerText}>Permanently deletes this profile and all its matches. This cannot be undone.</div>
          <button style={s.deleteBtn} type="button" onClick={handleDelete}>Delete this profile</button>
        </div>
      )}
    </div>
  );
}
